import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';

import InfoTooltipIcon from '@/components/icons/InfoTooltipIcon';
import { Gauge, gaugeClasses } from '@mui/x-charts';
import { formatNumber } from '@/utils/formatters';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { selectTimeRange } from '@/features/energyxplorer/redux/timeRangeSlice';
import { useSelector } from 'react-redux';
import { isOutOfBounds } from '@/features/energyxplorer/utils/timestampsSummaryHelpers';
import DistributionChart from './highcharts/DistributionChart';
import OpenInFullRoundedIcon from '@mui/icons-material/OpenInFullRounded';

const EnergyCoverageCard = ({
  title,
  description,
  children,
  energyOverviewDataResult,
}) => {
  const timeRange = useSelector(selectTimeRange);
  const shouldRenderHashedGauge = isOutOfBounds(
    energyOverviewDataResult.TimestampsSummary,
    timeRange,
  );

  const [openDistributionChart, setOpenDistributionChart] =
    React.useState(false);
  const handleOpenDistributionChart = () => {
    setOpenDistributionChart(true);
  };

  const analyzed =
    energyOverviewDataResult?.EnergyDataSummary?.MeasuredEnergyPercentage;

  return (
    <>
      <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent>
          <Stack
            direction="column"
            color="inherit"
            elevation={0}
            sx={{
              transition: 'box-shadow 0.3s ease',
              height: '100%',
              background: 'transparent',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    fontWeight="bold"
                  >
                    {title}
                  </Typography>
                  <InfoTooltipIcon title={description} />
                </Box>
              </Box>
              <IconButton onClick={handleOpenDistributionChart} size="small">
                <OpenInFullRoundedIcon />
              </IconButton>
            </Box>
            <div>
              {children && (
                <Box
                  sx={{
                    mt: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  {children}
                </Box>
              )}
              {shouldRenderHashedGauge ? (
                <HashedGauge
                  analyzed={analyzed} // your % value
                  width={200}
                  height={130}
                />
              ) : (
                <Gauge
                  width={200}
                  height={130}
                  value={analyzed ? analyzed : 0}
                  cornerRadius={'40%'}
                  startAngle={-110}
                  endAngle={110}
                  text={analyzed ? `${formatNumber(analyzed)}%` : '0%'}
                  sx={(theme) => {
                    const gaugeColor = '#52b202';
                    return {
                      [`& .${gaugeClasses.valueText}`]: { fontSize: 30 },
                      [`& .${gaugeClasses.valueArc}`]: {
                        fill: gaugeColor,
                        opacity: 0.8,
                      },
                      [`& .${gaugeClasses.referenceArc}`]: {
                        fill: theme.palette.text.disabled,
                        opacity: 0.5,
                      },
                      margin: '0 auto',
                    };
                  }}
                />
              )}
            </div>
          </Stack>
        </CardContent>
      </Card>
      <Dialog
        open={openDistributionChart}
        onClose={() => setOpenDistributionChart(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Energi Fordeling</DialogTitle>
        <DialogContent dividers>
          <DistributionChart
            data={energyOverviewDataResult?.EnergyDistributionData || []}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EnergyCoverageCard;

function HashedGauge({
  analyzed = 0,
  gaugeColor = '#52b202',
  width = 200,
  height = 130,
}) {
  const { t } = useTranslation('energyxplorer-energy-overview-tab');
  const patternId = useId().replace(/:/g, '_');

  return (
    <Tooltip title={t('energy_coverage_card.warning_tooltip')}>
      <Box position="relative">
        {/* Define the pattern BEFORE the Gauge so it's available when styles apply */}
        <svg width="0" height="0" aria-hidden focusable="false">
          <defs>
            <pattern
              id={patternId}
              patternUnits="userSpaceOnUse"
              width="12"
              height="12"
              patternTransform="rotate(45)"
            >
              {/* faint base tint */}
              <rect
                x="0"
                y="0"
                width="12"
                height="12"
                fill={gaugeColor}
                opacity="0.15"
              />
              {/* hash stripe */}
              <rect
                x="0"
                y="0"
                width="6"
                height="12"
                fill={gaugeColor}
                opacity="0.6"
              />
            </pattern>
          </defs>
        </svg>
        <Gauge
          width={width}
          height={height}
          value={analyzed || 0}
          cornerRadius="40%"
          startAngle={-110}
          endAngle={110}
          text={analyzed ? `${formatNumber(analyzed)}%` : '0%'}
          sx={(theme) => ({
            // Apply the hashed pattern to the arc — override MUI's default fill.
            [`& .${gaugeClasses.valueArc}`]: {
              fill: `url(#${patternId}) !important`,
              stroke: gaugeColor,
              strokeOpacity: 0.35,
              strokeWidth: 1,
              transition: 'all 300ms ease',
            },
            [`& .${gaugeClasses.referenceArc}`]: {
              fill: theme.palette.text.disabled,
              opacity: 0.4,
            },
            [`& .${gaugeClasses.valueText}`]: { fontSize: 30 },
            margin: '0 auto',
          })}
        />
      </Box>
    </Tooltip>
  );
}
